import { dataLayerConfig, TAGLink, useFacilityContext } from '@aspendental/shared-components-web';
import { FacilityFieldsFragment, NearbyFacility } from '@aspendental/shared-components-web/lib/graphql/__generated/sdk';
import Call from '@mui/icons-material/Call';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Box, Button, ButtonBase, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { DatePicker, LocalizationProvider, pickersLayoutClasses } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isToday, parse } from 'date-fns';
import en from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import SlotList from '../SlotList/SlotList';
import FilterAppointmentsDrawer from '@src/components/FilterAppointmentsDrawer/FilterAppointmentsDrawer';
import { RenderDistance } from '@src/components/FindOfficeDrawer/FindOffice.helpers';
import FindOfficeDrawer from '@src/components/FindOfficeDrawer/FindOfficeDrawer';
import { HydraState, SubmitAppointmentErrorCode } from '@src/constants';
import { BffBrandType, BffFacility, useBffNearbyFacilitiesByZipcodeQuery } from '@src/lib/__generated/beffe/sdk';
import { getLogger } from '@src/lib/logging/logger';
import { createAnswerObject } from '@src/pages/utils/create-answer-object';
import { getConsultAppointments } from '@src/services/getConsultAppointments';
import { useAppStore, usePatientInformationStore, useStepManagerStore } from '@src/store';
import useAppointmentInformationStore from '@src/store/appointment-information.store';
import useScheduleQuestionsStore from '@src/store/schedule-questions.store';
import DataLayerIntegrityCheck from '@src/utils/datalayer-integrity-check';
import { getPhoneNumber } from '@src/utils/get-phone-number';
import { Appointment } from 'types/tag';
interface ITimeSlotSelectionStepProps {
  leadId: string;
  setModalType: Dispatch<SetStateAction<'promptToCall' | 'existingConsultation'>>;
  enablePtcSlotFilter: boolean;
}
const findRelevantAppointmentMetrics = (appointmentData: any) => {
  if (!appointmentData) return;
  const {
    metrics
  } = appointmentData;
  if (!metrics) return;
  const {
    appointments
  } = metrics;
  if (!appointments) return;
  const {
    days,
    timeslots,
    unrestricted
  } = appointments;
  if (!unrestricted) {
    return {
      days,
      timeslots,
      unrestrictedDays: 0,
      unrestrictedTimeslots: 0
    };
  }
  return {
    days,
    timeslots,
    unrestrictedDays: unrestricted?.days,
    unrestrictedTimeslots: unrestricted?.timeslots
  };
};

// constant for number of default days to show and load at a time
const DAYS_OUT = 6;
export default function TimeSlotSelectionStep({
  leadId,
  setModalType,
  enablePtcSlotFilter
}: Readonly<ITimeSlotSelectionStepProps>) {
  const router = useRouter();
  const {
    t
  } = useTranslation('index');
  const theme = useTheme();
  const {
    questionData,
    questionsAnswers,
    setSchedulePopupError
  } = useScheduleQuestionsStore(store => store);
  const {
    setIsStepSubmitting,
    stepOnePtc
  } = useStepManagerStore(store => store);
  const {
    enableHydraCalls,
    sessionId
  } = useAppStore(store => store);
  const facilityDetails = useFacilityContext();
  const {
    chosenCenterData,
    setChosenCenterData,
    setChosenAppointmentData,
    setChosenAppointmentKey,
    shouldPreserveAppointmentAndCenterData,
    appointmentsToShow,
    setAppointmentsToShow,
    presetCenterID,
    presetCenterName,
    setTrackAppointmentTimesShown,
    setTrackAppointmentDaysShown,
    loadingAppointments,
    setLoadingAppointments
  } = useAppointmentInformationStore(store => store);
  const {
    zipCode,
    salesforceGuid,
    salesforceAccountId,
    salesforceS20,
    salesforceTier,
    setLeadSegment20,
    setSalesforceS20,
    setSalesforceTier
  } = usePatientInformationStore(store => store);
  const [apptData, setApptData] = useState<any>(null);
  const [findOfficeOpen, setFindOfficeOpen] = useState<boolean>(false);
  const [filterDaysOpen, setFilterDaysOpen] = useState<boolean>(false);
  const [filteredAppointments, setFilteredAppointments] = useState<any[]>([]);
  const [initialNearbyLocations, setInitialNearbyLocations] = useState<BffFacility[] | FacilityFieldsFragment[]>([]);
  // Appointments states for filters
  const [selectedStartDateFilter, setSelectedStartDateFilter] = useState<Date | null>(new Date());
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [timeOfDay, setTimeOfDay] = useState<string>('All');
  const [radiusMiles, setRadiusMiles] = useState<number>(250);
  const [timeoutError, setTimeoutError] = useState<string>('');
  const [ptcDaysOut, setPtcDaysOut] = useState(DAYS_OUT);
  const searchParams = useSearchParams();
  const [{
    data: facilitiesByZip,
    error: errorFacilitiesByZip,
    fetching: fetchingFacilitiesByZip
  }] = useBffNearbyFacilitiesByZipcodeQuery({
    variables: {
      zipCode,
      brand: BffBrandType.ClearChoice,
      radiusMiles
    },
    pause: searchParams.has('fc')
  });
  const mapNearbyOfficeDistance = (centerList: NearbyFacility[] | null) => {
    return centerList?.map(center => ({
      ...center.facility,
      location: {
        ...center.facility.location,
        distance: center.distanceInMiles
      }
    }));
  };
  useEffect(() => {
    if (apptData) {
      const appointmentFields = {
        appointment_time_slots: 0,
        appointment_days_shown: 0
      };
      const metrics = findRelevantAppointmentMetrics(apptData);
      if (metrics) {
        appointmentFields.appointment_time_slots = metrics.timeslots;
        appointmentFields.appointment_days_shown = metrics.days;
        if (enablePtcSlotFilter) {
          appointmentFields.appointment_time_slots = metrics.unrestrictedTimeslots;
          appointmentFields.appointment_days_shown = metrics.unrestrictedDays;
        }
        setTrackAppointmentTimesShown(appointmentFields.appointment_time_slots);
        setTrackAppointmentDaysShown(appointmentFields.appointment_days_shown);
      }
      dataLayerConfig({
        event: 'appointment_step',
        step_name: 'location_date_time_page_load',
        step_sequence: 3,
        lead_id: salesforceGuid,
        lead_tier: salesforceS20 && salesforceS20 != 21 ? salesforceTier : 'T1',
        lead_segment: salesforceS20 ?? '21',
        appointments_available: !!apptData?.appointments?.length,
        website_selected_center_name: presetCenterName,
        website_selected_center_id: presetCenterID,
        appointment_selected_center_name: chosenCenterData?.name,
        appointment_selected_center_id: chosenCenterData?.code,
        ptc: enablePtcSlotFilter ? '1' : '0',
        ...appointmentFields,
        hydra_status: enableHydraCalls ? HydraState.ON : HydraState.OFF
      });
      DataLayerIntegrityCheck('location_date_time_page_load', sessionId, enableHydraCalls);
    }
  }, [apptData]);
  useEffect(() => {
    if (facilitiesByZip?.facilitiesByZipcode) {
      if (!facilitiesByZip.facilitiesByZipcode.length) {
        setRadiusMiles(500);
      } else {
        const nearbyFacilities = facilitiesByZip?.facilitiesByZipcode;
        const [initialCenter] = nearbyFacilities;
        if (!shouldPreserveAppointmentAndCenterData) {
          setChosenCenterData(initialCenter);
        }
        if (nearbyFacilities.length) {
          setInitialNearbyLocations(nearbyFacilities as BffFacility[]);
        }
      }
    } else if (searchParams.has('fc')) {
      // use facility context only when having code in url
      setChosenCenterData(facilityDetails?.officeInfo);
      const nearbyCenters = mapNearbyOfficeDistance(facilityDetails?.locationState?.nearByOfficeLocations ?? []) as BffFacility[];
      if (nearbyCenters.length) {
        setInitialNearbyLocations(nearbyCenters);
      }
    }
  }, [facilitiesByZip, facilityDetails, shouldPreserveAppointmentAndCenterData, setChosenCenterData, setInitialNearbyLocations, searchParams]);
  useEffect(() => {
    if (!leadId || !chosenCenterData) return;
    const controller = new AbortController();
    const signal = controller.signal;
    setLoadingAppointments(true);

    // Create a 30 second timeout promise to handle long-running API calls
    const timeoutId = setTimeout(() => {
      setLoadingAppointments(false);
      setIsStepSubmitting(false);
      const logger = getLogger('app');
      logger.info('Get Appointments Timeout');
      setTimeoutError(t('somethingWentWrong'));
    }, 30000);
    const answerObject = createAnswerObject(questionData, questionsAnswers);

    // Fetch appointments data
    getConsultAppointments({
      leadId: leadId,
      salesforceId: salesforceGuid ?? '',
      startDate: format(selectedStartDateFilter ?? new Date(), 'yyyy-MM-dd'),
      centerId: chosenCenterData?.code?.replace('CC_', ''),
      signal,
      answers: answerObject,
      enableHydra: enableHydraCalls,
      sessionId
    }).then(data => {
      if (!signal.aborted) {
        setPtcDaysOut(data?.metrics?.appointments?.unrestricted?.days ?? DAYS_OUT);
        clearTimeout(timeoutId);
        setTimeoutError('');
        setApptData(data);
        setLoadingAppointments(false);
        setIsStepSubmitting(false);
        dataLayerConfig({
          event: 'scheduleform_datapass_partialleadcreated',
          salesforce_guid: salesforceGuid,
          salesforce_account_id: salesforceAccountId,
          language_preference: router.locale ?? 'en-US'
        });
        if (stepOnePtc) {
          setSchedulePopupError(true);
          setModalType('promptToCall');
        }
        if (data.status === SubmitAppointmentErrorCode.CONSULT_SCHEDULED) {
          setModalType('existingConsultation');
          setSchedulePopupError(true);
          return;
        }
        if (data.status === SubmitAppointmentErrorCode.NO_APPOINTMENTS) {
          setModalType('promptToCall');
          setSchedulePopupError(true);
        }
        setLeadSegment20(data.leadScoreInfo?.leadSegment20);
        const {
          s20,
          tier
        } = data.score;
        setSalesforceS20(s20);
        setSalesforceTier(`T${tier}`);
        dataLayerConfig({
          event: 'Lead Segment 20',
          lead_segment20: s20,
          salesforce_guid: salesforceGuid,
          salesforce_account_id: salesforceAccountId,
          language_preference: router.locale ?? 'en-US'
        });
      }
    }).catch(error => {
      if (!signal.aborted) {
        console.error('Failed Loading Consults Appointments Data:', error);
        clearTimeout(timeoutId);
        setTimeoutError('');
        setLoadingAppointments(false);
        setIsStepSubmitting(false);
        setModalType('promptToCall');
        setSchedulePopupError(true);
      }
    });
    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [leadId, chosenCenterData, selectedStartDateFilter]);
  useEffect(() => {
    if (selectedStartDateFilter && apptData?.appointments) {
      let newFilteredAppointments = Object.entries(apptData.appointments[0].appointments);

      // Filter by selected days
      if (selectedDays.length > 0) {
        newFilteredAppointments = newFilteredAppointments.filter(([_, value]: [string, any]) => selectedDays.includes(value.day));
      }

      // Filter by time of day
      if (timeOfDay !== 'All') {
        newFilteredAppointments = newFilteredAppointments.map(([key, value]: any) => {
          const filteredTimeslots = Object.entries(value.timeslots).filter(([, timeslot]: any) => timeslot.time?.toUpperCase().includes(timeOfDay));
          if (filteredTimeslots.length > 0) {
            return [key, {
              ...value,
              timeslots: Object.fromEntries(filteredTimeslots)
            }];
          }
          return null;
        }).filter(Boolean);
      }
      if (!shouldPreserveAppointmentAndCenterData) {
        setChosenAppointmentKey('');
        setChosenAppointmentData(null);
        setAppointmentsToShow(DAYS_OUT);
      }
      setFilteredAppointments(newFilteredAppointments);
    } else {
      setFilteredAppointments([]);
    }
  }, [selectedStartDateFilter, selectedDays, timeOfDay, apptData, setChosenAppointmentData, setChosenAppointmentKey, shouldPreserveAppointmentAndCenterData, setAppointmentsToShow]);
  const dateInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // Blur the date input on mount to prevent auto-focus
    if (dateInputRef.current) {
      dateInputRef.current.blur();
    }
  }, []);
  return <>
			<FindOfficeDrawer open={findOfficeOpen} onClose={() => setFindOfficeOpen(false)} handleChooseLocation={data => {
      setChosenCenterData(data);
      setFindOfficeOpen(false);
    }} initialNearbyLocations={initialNearbyLocations} data-sentry-element="FindOfficeDrawer" data-sentry-source-file="TimeSlotSelectionStep.tsx" />
			<FilterAppointmentsDrawer selectedDays={selectedDays} timeOfDay={timeOfDay} handleApply={(days: string[], timeOfDay: string) => {
      setSelectedDays(days);
      setTimeOfDay(timeOfDay);
      setFilterDaysOpen(false);
    }} open={filterDaysOpen} onClose={() => setFilterDaysOpen(false)} data-sentry-element="FilterAppointmentsDrawer" data-sentry-source-file="TimeSlotSelectionStep.tsx" />
			{errorFacilitiesByZip ? <>
					<Typography variant="bodyMediumBook" mb={6} mt={2} sx={{
        display: 'block'
      }}>
						{t('somethingWentWrong')}
					</Typography>
				</> : <Box sx={{
      borderRadius: {
        md: 1,
        xs: 2
      },
      py: {
        md: '12px',
        xs: '11px'
      },
      px: '16px',
      maxWidth: '525px',
      border: '1px solid',
      borderColor: 'grey.A400',
      mb: {
        md: 3,
        xs: 3.5
      },
      mt: {
        md: 3,
        xs: 2
      }
    }}>
					<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{
        mb: 0.75
      }}>
						{fetchingFacilitiesByZip ? <Skeleton height={28} width={140} /> : <Typography variant="bodyMediumSemiBold" sx={{
          color: 'text.primary'
        }}>
								ClearChoice {chosenCenterData?.name}
							</Typography>}
						{RenderDistance(chosenCenterData, 'text.secondary', t('miles'))}
					</Stack>
					<Stack direction={{
        sm: 'row',
        xs: 'column'
      }} justifyContent="space-between" alignItems={{
        sm: 'flex-end',
        xs: 'column'
      }}>
						<Box>
							{fetchingFacilitiesByZip ? <Skeleton height={28} width={140} /> : <Typography component={Link} sx={{
            color: 'text.interactive',
            fontSize: '14px',
            textDecoration: 'underline'
          }} variant="bodySmallBook" href={`https://www.google.com/maps?q=${encodeURIComponent(`${chosenCenterData?.address?.address1} ${chosenCenterData?.address?.city}, ${chosenCenterData?.address?.stateCode} ${chosenCenterData?.address?.zipCode}`)}`} target="_blank" rel="noopener noreferrer">
									{chosenCenterData?.address?.address1} {chosenCenterData?.address?.city},{' '}
									{chosenCenterData?.address?.stateCode} {chosenCenterData?.address?.zipCode}
								</Typography>}
							{fetchingFacilitiesByZip ? <Skeleton height={28} width={140} /> : <Typography sx={{
            color: 'text.interactive',
            display: 'block',
            mt: {
              md: 0.5,
              xs: 0.25
            },
            fontSize: '14px',
            textDecoration: 'underline'
          }} variant="bodySmallBook">
									<TAGLink href={`tel:${getPhoneNumber(chosenCenterData)}`} linkSx={{
              color: 'text.interactive'
            }}>
										{getPhoneNumber(chosenCenterData)}
									</TAGLink>
								</Typography>}
						</Box>
						{fetchingFacilitiesByZip ? <Skeleton height={42} sx={{
          mt: {
            md: 2,
            xs: '5px'
          }
        }} width={180} /> : <Button variant="primaryHC" sx={{
          border: '1px solid',
          minWidth: '180px',
          mt: {
            md: 2,
            xs: '5px'
          }
        }} onClick={() => setFindOfficeOpen(true)} size="S">
								{t('changeLocation')}
							</Button>}
					</Stack>
				</Box>}

			<Stack direction="row" spacing={2} maxWidth="450px" alignItems="center" mb={{
      md: 4,
      xs: 2
    }} data-sentry-element="Stack" data-sentry-source-file="TimeSlotSelectionStep.tsx">
				<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={router.locale === 'es-US' ? es : en} data-sentry-element="LocalizationProvider" data-sentry-source-file="TimeSlotSelectionStep.tsx">
					<DatePicker label={t('calendar')} name="calendar" className={pickersLayoutClasses.root} value={selectedStartDateFilter} minDate={new Date()} onChange={newValue => {
          setIsStepSubmitting(true);
          setSelectedStartDateFilter(newValue ?? new Date());
        }} format="EEEE, MMMM d" slotProps={{
          textField: {
            inputRef: dateInputRef
          }
        }} data-sentry-element="DatePicker" data-sentry-source-file="TimeSlotSelectionStep.tsx" />
				</LocalizationProvider>
				<Button onClick={() => setFilterDaysOpen(true)} variant="primaryHC" sx={{
        border: '1px solid'
      }} size="M" data-sentry-element="Button" data-sentry-source-file="TimeSlotSelectionStep.tsx">
					<FilterListIcon sx={{
          width: '20px',
          height: '20px',
          mr: 0.5
        }} data-sentry-element="FilterListIcon" data-sentry-source-file="TimeSlotSelectionStep.tsx" />
					{t('filter')}
				</Button>
			</Stack>
			{!loadingAppointments && !apptData?.appointments && timeoutError === '' || stepOnePtc && <Typography>{t('noAppointmentsAvailable')}</Typography>}
			{!loadingAppointments && !apptData?.appointments && timeoutError !== '' && <Typography my={4}>{timeoutError}</Typography>}
			{apptData?.appointments && (filteredAppointments.length === 0 || filteredAppointments.every(([_key, value]) => value.open === 0)) ? <Typography>{t('noMatchingAppointments')}</Typography> : filteredAppointments.filter(([_key, value]) => value.open !== 0).slice(0, appointmentsToShow).map(([key, value]: [string, Appointment]) => {
      const filteredTimeslots = Object.entries(value.timeslots).filter(([_key, value]) => {
        // If the enablePtcSlotFilter flag is true, show only slots w/ PTC === 0, otherwise include all
        return !enablePtcSlotFilter || !value.ptc;
      }).reduce((acc, [slotKey, slotValue]) => {
        acc[slotKey] = slotValue;
        return acc;
      }, {});
      if (stepOnePtc) {
        return null;
      }
      return <SlotList key={key} loading={false} slots={filteredTimeslots} title={isToday(parse(key, 'yyyy-MM-dd', new Date())) ? t('today') : new Intl.DateTimeFormat(router.locale || 'en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      }).format(new Date(new Date(key).toISOString().slice(0, 19)))} noSlotsMsg={t('noAppointmentsAvailable')} swiperId={key} />;
    })}
			{(enablePtcSlotFilter && ptcDaysOut > appointmentsToShow || !enablePtcSlotFilter && appointmentsToShow < filteredAppointments.length) && <Button onClick={() => setAppointmentsToShow(appointmentsToShow + DAYS_OUT)} variant="primaryHC" size="M" sx={{
      mx: 'auto',
      mb: 4,
      border: '1px solid',
      display: 'block'
    }}>
					{t('loadMore')}
				</Button>}

			{!loadingAppointments && <Stack direction="column" spacing={1.5} pb={1} mt={1} alignItems="center" sx={{
      padding: '20px',
      borderRadius: '8px',
      background: theme.palette.info.light,
      textAlign: 'center'
    }}>
					<Typography variant="header2">{t('cannotFindAppointment')}</Typography>
					<Typography variant="bodyLargeBook" sx={{
        color: 'text.primary',
        textDecoration: 'none'
      }}>
						{t('pleaseCallAt')}{' '}
						<TAGLink href={`tel:888-651-9950`} linkSx={{
          color: 'text.interactive',
          textDecoration: 'none'
        }}>
							888-651-9950
						</TAGLink>{' '}
						{t('orSelectNewCenter')}
					</Typography>
					<Button variant="primaryHC" size="S" sx={{
        background: 'transparent',
        border: '1px solid',
        borderRadius: '100px'
      }} href={`tel:888-651-9950`}>
						<Call sx={{
          mr: 0.5
        }} />
						{t('callUs')}
					</Button>
					<ButtonBase onClick={() => setFindOfficeOpen(true)}>
						<Typography variant="bodyLargeBook" sx={{
          color: 'text.primary'
        }}>
							{t('findAnotherCenter')}
						</Typography>
					</ButtonBase>
				</Stack>}
		</>;
}